import { strokeToWire, type Notebook, type Page, type StrokeWire, type SyncOp, type TextBlock } from 'shared';
import { api, ApiError } from '../api.js';
import { db, getMeta, setMeta } from '../db/db.js';

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error' | 'unauthenticated';

interface PullResponse {
  cursor: number;
  more: boolean;
  notebooks: Notebook[];
  pages: Page[];
  strokes: StrokeWire[];
  textBlocks: TextBlock[];
}

type Listener = (state: SyncState, pending: number, lastAt: number | null) => void;

class Syncer {
  private listeners = new Set<Listener>();
  private state: SyncState = 'idle';
  private lastAt: number | null = null;
  private running = false;
  private again = false;
  private timer: number | null = null;

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    void this.notify();
    return () => this.listeners.delete(fn);
  }

  private async notify(): Promise<void> {
    const pending = await db.outbox.count();
    for (const fn of this.listeners) fn(this.state, pending, this.lastAt);
  }

  /** Agenda um push depois de 5 s parado, como manda a SPEC §13.1. */
  schedule(delayMs = 5000): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = window.setTimeout(() => void this.run(), delayMs);
  }

  async run(): Promise<void> {
    if (this.running) { this.again = true; return; }
    this.running = true;
    this.state = 'syncing';
    await this.notify();
    try {
      await this.push();
      await this.pull();
      this.state = 'idle';
      this.lastAt = Date.now();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) this.state = 'unauthenticated';
      else if (!navigator.onLine || err instanceof TypeError) this.state = 'offline';
      else this.state = 'error';
    } finally {
      this.running = false;
      await this.notify();
      if (this.again) { this.again = false; this.schedule(500); }
    }
  }

  private async push(): Promise<void> {
    // Lotes de 400: um envio único com a fila inteira depois de um dia
    // offline estouraria o limite de corpo da API.
    for (;;) {
      const rows = await db.outbox.orderBy('seq').limit(400).toArray();
      if (!rows.length) return;
      const res = await api.post<{ applied: string[]; failed: { id: string }[] }>('/api/sync/push', {
        ops: rows.map((r) => r.op),
      });
      const seqs = rows.map((r) => r.seq).filter((s): s is number => typeof s === 'number');
      await db.outbox.bulkDelete(seqs);
      if (res.failed?.length) console.warn('operações recusadas pelo servidor', res.failed);
      if (rows.length < 400) return;
    }
  }

  private async pull(): Promise<void> {
    for (;;) {
      const since = await getMeta<number>('syncCursor', 0);
      const res = await api.get<PullResponse>(`/api/sync/pull?since=${since}`);
      await db.transaction('rw', db.notebooks, db.pages, db.strokes, db.textBlocks, db.meta, async () => {
        if (res.notebooks.length) await db.notebooks.bulkPut(res.notebooks);
        if (res.pages.length) await db.pages.bulkPut(res.pages);
        if (res.strokes.length) await db.strokes.bulkPut(res.strokes);
        if (res.textBlocks?.length) await db.textBlocks.bulkPut(res.textBlocks);
        await db.meta.put({ key: 'syncCursor', value: res.cursor });
      });
      if (!res.more) return;
    }
  }

  /** Reenvia tudo do zero — usado depois de reinstalar o app. */
  async fullPull(): Promise<void> {
    await setMeta('syncCursor', 0);
    await this.run();
  }
}

export const syncer = new Syncer();

export function enqueue(op: SyncOp): Promise<number> {
  return db.outbox.add({ op });
}

export function strokeOp(stroke: Parameters<typeof strokeToWire>[0]): SyncOp {
  const wire = strokeToWire(stroke);
  return { op: 'put', entity: 'stroke', id: wire.id, payload: wire, at: Date.now() };
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void syncer.run());
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void syncer.run();
  });
}
